import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Hotel, PmsType } from "@/types/hotel";
import { PMS_PROVIDER_LABEL_KEYS } from "@/utils/pmsProviderLabels";
import { useAuthenticatedUser } from "@/hooks/useAuthenticatedUser";
import { useTenants } from "@/components/pages/tenants/hooks/useTenants";
import { useCopyHotel } from "@/hooks/useSystemCopy";
import { CopyHotelOverridesDto } from "@/types/system-copy";

interface HotelCopyDialogProps {
  hotel: Hotel | null;
  /** Tenant of Hotel Brands in tenant mode (`?tenantId=`), sent in `x-tenant-id`. */
  tenantId?: string;
  onClose: () => void;
}

const PMS_VALUES: PmsType[] = Object.values(PmsType);

export const HotelCopyDialog: React.FC<HotelCopyDialogProps> = ({
  hotel,
  tenantId,
  onClose,
}) => {
  const { t } = useTranslation();
  const { userProfile } = useAuthenticatedUser();
  const isSuperAdmin = userProfile?.role === "SUPER_ADMIN";
  const { mutateAsync, isPending } = useCopyHotel(tenantId);

  const { data: tenantsResponse } = useTenants(
    { limit: 1000, sortBy: "name", sortOrder: "asc" },
    !!hotel && isSuperAdmin,
  );

  const [name, setName] = useState("");
  const [pms, setPms] = useState<PmsType | undefined>(undefined);
  const [targetTenantId, setTargetTenantId] = useState<string>("");

  const tenantOptions = useMemo(
    () => tenantsResponse?.items ?? [],
    [tenantsResponse],
  );

  useEffect(() => {
    if (hotel) {
      setName(`${hotel.name} (Copy)`);
      setPms(hotel.pms);
      setTargetTenantId(hotel.tenantId ?? "");
    }
  }, [hotel]);

  const handleClose = () => {
    if (isPending) return;
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!hotel) return;

    const trimmedName = name.trim();
    const overrides: CopyHotelOverridesDto = {};
    if (trimmedName) {
      overrides.name = trimmedName;
    }
    if (pms && pms !== hotel.pms) {
      overrides.pms = pms;
    }

    try {
      const result = await mutateAsync({
        sourceHotelId: hotel.id,
        ...(isSuperAdmin && targetTenantId && targetTenantId !== hotel.tenantId
          ? { targetTenantId }
          : {}),
        ...(Object.keys(overrides).length > 0 ? { overrides } : {}),
      });

      const { summary } = result;
      toast.success(
        t("hotels.copy.successToast", {
          categories: summary.roomCategoryCount + summary.serviceCategoryCount,
          amenities: summary.amenityCount,
          services: summary.hotelServiceCount,
          files: summary.fileCount,
        }),
      );

      if (!summary.subscriptionCopied) {
        toast.info(t("hotels.copy.successSubscriptionSkipped"));
      }
      if (summary.fileSkippedCount > 0) {
        toast.warning(
          t("hotels.copy.successFileSkipped", {
            count: summary.fileSkippedCount,
          }),
        );
      }
      if (hotel.paymentSettings?.paymentEnabled) {
        toast.info(t("hotels.copy.successPaymentsReset"));
      }

      onClose();
    } catch {
      toast.error(t("hotels.copy.errorToast"));
    }
  };

  const showPmsSelect = PMS_VALUES.length >= 2;

  return (
    <Dialog open={!!hotel} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("hotels.copy.title")}</DialogTitle>
          {hotel ? (
            <DialogDescription>
              {t("hotels.copy.description", { name: hotel.name })}
            </DialogDescription>
          ) : null}
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <Input
            label={t("hotels.copy.name")}
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />

          {showPmsSelect ? (
            <div className="flex flex-col gap-1.5">
              <span className="font-medium text-sm leading-none text-zinc-900">
                {t("hotels.copy.pms")}
              </span>
              <Select
                value={pms ?? ""}
                onValueChange={(value) => setPms(value as PmsType)}
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PMS_VALUES.map((value) => (
                    <SelectItem key={value} value={value}>
                      {t(PMS_PROVIDER_LABEL_KEYS[value])}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : null}

          {isSuperAdmin ? (
            <div className="flex flex-col gap-1.5">
              <span className="font-medium text-sm leading-none text-zinc-900">
                {t("hotels.copy.targetTenant")}
              </span>
              <Select
                value={targetTenantId}
                onValueChange={setTargetTenantId}
                disabled={tenantOptions.length === 0}
              >
                <SelectTrigger className="w-full">
                  <SelectValue
                    placeholder={t("hotels.copy.targetTenantPlaceholder")}
                  />
                </SelectTrigger>
                <SelectContent>
                  {tenantOptions.map((tenant) => (
                    <SelectItem key={tenant.id} value={tenant.id}>
                      {`${tenant.name} (${tenant.slug})`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {t("hotels.copy.targetTenantHint")}
              </p>
            </div>
          ) : null}

          <p className="text-xs text-muted-foreground">
            {t("hotels.copy.progressHint")}
          </p>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={isPending}
              onClick={handleClose}
            >
              {t("hotels.copy.cancel")}
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending ? t("hotels.copy.saving") : t("hotels.copy.save")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
